import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { difference, isNull } from "lodash"
import { subscribe } from "lib/live_events"

import { addUnique } from "../utils"
import getRecentMessages from '../../libs/api/unreadMessages'
import importData from '../../libs/store/utils/import_data'


const UnreadContext = createContext(null)

export function UnreadProvider({ children }) {
  const dispatch = useDispatch()

  const [messages, setMessages] = useState([])
  const [recentMessages, setRecentMessages] = useState([])
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [autoMerge, setAutoMerge] = useState(true)

  const pullFromServer = useCallback(async (page = 0) => {
    try {
      const { data } = await getRecentMessages({ page })
      importData(dispatch, data.data)

      setMessages(messages => addUnique(messages, data.list))
      setRecentMessages([])
      setTotal(data.total)
      setLoading(false)
    } catch (err) {
      console.error("Failed to fetch messages:", err)
    }
  }, [dispatch])

  const refresh = useCallback(() => {
    setMessages([])
    setRecentMessages([])
    setTotal(null)
    setLoading(true)
    pullFromServer(0)
  }, [pullFromServer])

  const mergeNew = useCallback(() => {
    if (recentMessages.length > 0) {
      setMessages([... recentMessages, ...messages])
      setRecentMessages([])
    }
  }, [messages, recentMessages])

  // 🔹 Subscribe to new comments
  useEffect(() => {
    const unsub = subscribe("messaging.new_comment", ({ id: comment_id }) => {
      // Add to context-managed "recent" list
      if (autoMerge)
        setMessages(prev => [ comment_id, ...prev])
      else
        setRecentMessages(prev => [ comment_id, ...prev ])
    })
    return () => unsub?.()
  }, [autoMerge, setMessages, setRecentMessages])

  useEffect(() => {
    if (autoMerge) 
      mergeNew()
  }, [ autoMerge, mergeNew ])

  useEffect(() => {
    const unsub = subscribe("messaging.read_thread", ({ read_messages }) => {
      setMessages(messages => difference(messages, read_messages))
      setRecentMessages(messages => difference(messages, read_messages))
    })

    return () => unsub?.()
  }, [setMessages, setRecentMessages])

  // 🔹 Initial fetch
  useEffect(() => {
    if (isNull(page)) {
      setMessages([])
      setTotal(null)
      return
    }

    setLoading(true)
    pullFromServer(page)
  }, [page, pullFromServer])

    
  const setNewPage = useCallback(newPage => {
    setPage(page => !newPage || !page || newPage > page ? newPage : page)
  }, [setPage])

  const value = useMemo(() => ({ 
    messages, recentMessages, page, setPage: setNewPage, setAutoMerge, loading, refresh, mergeNew, allLoaded: messages.length == total
  }), [messages, recentMessages, page, setNewPage, setAutoMerge, loading, refresh, mergeNew, total])

  return (
    <UnreadContext.Provider value={value}>
      {children}
    </UnreadContext.Provider>
  )
}

export function useUnreadContext() {
  return useContext(UnreadContext)
}
